<template>
  <div
    class="insights-navigation-list-card d-flex align-items-center cursor-pointer"
    :class="{ active: isActive }"
    @click="toggleNavigation"
  >
    <div class="relative">
      <AppAvatar
        v-if="hasAvatar"
        :avatar="item.avatar"
        size="medium"
      />
      <AppAvatarInitial
        v-else
        :text="item.name"
        size="medium"
      />
      <span
        v-if="isMemberCard"
        :class="isActiveClasses"
      />
    </div>
    <div class="details d-flex flex-column w-full has-engagement">
      <div class="name d-flex align-items-center justify-content-between">
        <span class="string-truncate">{{ item.name }}</span>
      </div>
      <span class="description">{{ description }}</span>
    </div>
    <div
      v-if="loadingEngagement"
      class="skeleton-item skeleton-item-rounded engagement-item"
    />
    <div
      v-else
      v-tooltip="tooltipOptions"
      :title="tooltip"
      class="engagement-score"
      :class="engagementScoreClass"
    >
      <AppChartDonut
        :values="chartData"
        :text="engagementText"
        :size="chartSize"
        :inner-radius="chartRadius"
        :sort="false"
      />
    </div>
  </div>
</template>

<script>
import AppAvatar from '~/components/AppAvatar.vue';
import AppAvatarInitial from '~/components/AppAvatarInitial.vue';
import { mapState, mapGetters } from 'vuex';
import { InsightsFilters } from '~/mixins';
import { INSIGHTS } from '~/app_constants';

import { pluralize } from '~/utils/string_utils';

export default {
  name: 'InsightsNavigationListCard',
  components: {
    AppAvatar,
    AppAvatarInitial,
  },
  mixins: [InsightsFilters],
  props: {
    item: {
      type: Object,
      required: true,
    },
    type: {
      type: String,
      required: true,
    },
    handleClick: {
      type: Function,
    },
    active: {
      type: Boolean,
    },
    teamId: {
      type: String,
    },
  },
  data: () => ({
    chartSize: 32,
    chartRadius: 13,
  }),
  computed: {
    ...mapGetters(['organizationId', 'users']),
    ...mapState(['filters', 'engagementScores', 'engagementEmptyReasons', 'loadingEngagement']),
    isMemberCard() {
      return this.type === 'user';
    },
    isActive() {
      return this.active || (this.isMemberCard && this.item.id === parseInt(this.filters.user_id));
    },
    description() {
      return this.item?.total_members
        ? `${this.item.total_members} ${pluralize(this.item.total_members, 'member')}`
        : this.item.job_title;
    },
    hasAvatar() {
      return this.item.avatar && !this.item.avatar?.normal?.includes('default');
    },
    isActiveClasses() {
      return {
        status: true,
        'status-active': this.item?.active_now,
      };
    },
    hasEngagementScore() {
      return typeof this.engagementScore === 'number' && this.engagementScore >= 0;
    },
    hasEngagementEmptyReason() {
      return this.engagementEmptyReason !== '';
    },
    engagementText() {
      return this.hasEngagementScore ? `${Math.round(this.engagementScore)}` : '-';
    },
    engagementScore() {
      if (this.isMemberCard) {
        const item = this.engagementScores?.find((score) => score[`${this.type}_id`] === this.item.id);

        return item ? Number(item.score) * 100 : null;
      }

      return this.lazyTeamEngagementScore;
    },
    lazyTeamEngagementScore() {
      const lazyTeamMembersId = this.item.team_member_ids;

      if (!lazyTeamMembersId?.length) {
        return null;
      }

      const teamScores = lazyTeamMembersId
        .map((memberId) => {
          const engagement = this.engagementScores?.find((score) => score.user_id === memberId);

          return engagement ? Number(engagement.score) * 100 : null;
        })
        .filter((score) => {
          return score !== null;
        });
      const average =
        teamScores?.reduce((acc, score) => {
          return acc + score;
        }, 0) / teamScores?.length || null;

      return average;
    },
    engagementScoreClass() {
      return INSIGHTS.engagement_ranges.find((range, index, array) => {
        const isLastRange = index === array.length - 1;

        if (!isLastRange) {
          return this.engagementScore >= range.value && this.engagementScore < array[index + 1]?.value;
        }

        return this.engagementScore >= range.value;
      })?.key;
    },
    chartData() {
      // If the score surpasses 100, we want to 'reset' the chart values to a 0 - 100 scale
      // This makes it easier to style the chart with the custom classes for each range
      const value = this.engagementScore > 100 ? this.engagementScore - 100 : this.engagementScore || 0;

      return [
        { key: 'score', value },
        { key: 'empty', value: 100 - value },
      ];
    },
    engagementEmptyReason() {
      if (this.isMemberCard) {
        if (this.engagementEmptyReasons) {
          const item = this.engagementEmptyReasons.find(
            (empty_reason) => empty_reason[`${this.type}_id`] === this.item.id,
          );

          return item ? item.empty_reason : '';
        }

        return '';
      }

      return '';
    },
    tooltip() {
      if (this.hasEngagementEmptyReason && !this.hasEngagementScore) {
        return INSIGHTS.tooltips.engagementScore.emptyReasons[this.engagementEmptyReason].content;
      }

      return '';
    },
    tooltipOptions() {
      return Object.freeze({
        html: true,
        sanitize: false,
        placement: 'left',
        template: INSIGHTS.tooltips.engagementScore.template,
        delay: { show: 0, hide: 2000 },
      });
    },
  },
  methods: {
    toggleNavigation() {
      const string = `${this.item.id}_${this.type}_id`;

      this.setMembersWithPreviousView(string, this.teamId && `${this.teamId}_team_id`);
    },
  },
};
</script>
